import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';

export default function PersonalDataPolicyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="mb-6 gap-2 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Назад
        </Button>

        <article className="prose prose-sm dark:prose-invert max-w-none">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">
            Политика обработки персональных данных
          </h1>
          <p className="text-muted-foreground text-sm mb-8">
            Последнее обновление: 10 июля 2025 г.
          </p>

          {/* 1. Общие положения */}
          <section className="space-y-4 mb-8">
            <h2 className="text-xl font-semibold border-b border-border pb-2">
              1. Общие положения
            </h2>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Настоящая Политика обработки персональных данных (далее — «Политика»)
              разработана в соответствии с Федеральным законом от 27.07.2006 № 152-ФЗ
              «О персональных данных» и определяет порядок обработки и защиты персональных
              данных пользователей сервиса <strong>YaDela</strong> (далее — «Сервис», «Оператор»).
            </p>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Оператор ставит своей важнейшей целью и условием осуществления своей
              деятельности соблюдение прав и свобод человека и гражданина при обработке
              его персональных данных, в том числе защиты прав на неприкосновенность
              частной жизни, личную и семейную тайну.
            </p>
          </section>

          {/* 2. Перечень обрабатываемых данных */}
          <section className="space-y-4 mb-8">
            <h2 className="text-xl font-semibold border-b border-border pb-2">
              2. Перечень обрабатываемых персональных данных
            </h2>
            <p className="text-sm text-foreground/90 leading-relaxed">
              При использовании Сервиса и оформлении подписки Оператор может обрабатывать
              следующие персональные данные:
            </p>
            <ul className="text-sm text-foreground/90 space-y-2 list-disc list-inside">
              <li>Фамилия, имя;</li>
              <li>Адрес электронной почты (email);</li>
              <li>Номер телефона (при указании в профиле);</li>
              <li>Данные аккаунтов социальных сетей (при авторизации через OAuth);</li>
              <li>Данные банковской карты (обрабатываются платёжным провайдером АО «Тинькофф Банк», Оператор не хранит полные данные карт);</li>
              <li>IP-адрес, тип и версия браузера, часовой пояс;</li>
              <li>Данные, добавленные пользователем в Сервис (информация о заказах, клиентах, задачах и иные бизнес-данные).</li>
            </ul>
          </section>

          {/* 3. Цели обработки */}
          <section className="space-y-4 mb-8">
            <h2 className="text-xl font-semibold border-b border-border pb-2">
              3. Цели обработки персональных данных
            </h2>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Персональные данные обрабатываются в следующих целях:
            </p>
            <ul className="text-sm text-foreground/90 space-y-2 list-disc list-inside">
              <li>Регистрация и идентификация пользователя в Сервисе;</li>
              <li>Предоставление доступа к функциям Сервиса;</li>
              <li>Обработка платежей и управление подпиской;</li>
              <li>Отправка уведомлений (электронная почта, Telegram) по запросу пользователя;</li>
              <li>Техническая поддержка и обработка обращений;</li>
              <li>Улучшение качества Сервиса и пользовательского опыта;</li>
              <li>Выполнение обязательств перед пользователем по предоставлению оплаченных услуг;</li>
              <li>Соблюдение требований законодательства Российской Федерации.</li>
            </ul>
          </section>

          {/* 4. Правовые основания */}
          <section className="space-y-4 mb-8">
            <h2 className="text-xl font-semibold border-b border-border pb-2">
              4. Правовые основания обработки
            </h2>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Обработка персональных данных осуществляется на следующих правовых основаниях:
            </p>
            <ul className="text-sm text-foreground/90 space-y-2 list-disc list-inside">
              <li>Согласие субъекта персональных данных (ст. 6 ч. 1 п. 1 ФЗ-152);</li>
              <li>Исполнение договора, стороной которого является субъект персональных данных (ст. 6 ч. 1 п. 5 ФЗ-152);</li>
              <li>Законные интересы Оператора, не нарушающие права и свободы субъекта персональных данных.</li>
            </ul>
          </section>

          {/* 5. Порядок обработки */}
          <section className="space-y-4 mb-8">
            <h2 className="text-xl font-semibold border-b border-border pb-2">
              5. Порядок обработки и хранения
            </h2>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Обработка персональных данных включает: сбор, запись, систематизацию,
              накопление, хранение, уточнение (обновление, изменение), извлечение,
              использование, передачу (предоставление, доступ), обезличивание,
              блокирование, удаление и уничтожение.
            </p>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Персональные данные хранятся в зашифрованном виде на защищённых серверах.
              Пароли хранятся в хешированном виде (bcrypt). Передача данных осуществляется
              по защищённым каналам связи (HTTPS/TLS).
            </p>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Срок хранения персональных данных — в течение срока действия учётной записи
              пользователя и в течение 3 (трёх) лет после её удаления, если иное не
              предусмотрено законодательством Российской Федерации.
            </p>
          </section>

          {/* 6. Передача третьим лицам */}
          <section className="space-y-4 mb-8">
            <h2 className="text-xl font-semibold border-b border-border pb-2">
              6. Передача данных третьим лицам
            </h2>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Оператор не продаёт и не передаёт персональные данные третьим лицам
              без согласия пользователя, за исключением следующих случаев:
            </p>
            <ul className="text-sm text-foreground/90 space-y-2 list-disc list-inside">
              <li>
                <strong>Платёжный провайдер</strong> — АО «Тинькофф Банк» (Т-Банк)
                для обработки платежей и управления подпиской. Банк обрабатывает данные
                карты в соответствии с требованиями PCI DSS;
              </li>
              <li>
                <strong>Интеграции</strong>, активированные пользователем
                (Telegram-бот, email-уведомления через Resend);
              </li>
              <li>
                <strong>По требованию законодательства</strong> Российской Федерации
                (по запросу уполномоченных государственных органов).
              </li>
            </ul>
          </section>

          {/* 7. Защита данных */}
          <section className="space-y-4 mb-8">
            <h2 className="text-xl font-semibold border-b border-border pb-2">
              7. Меры по защите персональных данных
            </h2>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Оператор принимает следующие организационные и технические меры для защиты
              персональных данных:
            </p>
            <ul className="text-sm text-foreground/90 space-y-2 list-disc list-inside">
              <li>Шифрование данных при передаче (HTTPS/TLS);</li>
              <li>Хеширование паролей (bcrypt);</li>
              <li>Ограничение доступа к персональным данным;</li>
              <li>Регулярное обновление программного обеспечения;</li>
              <li>Журналирование действий с персональными данными;</li>
              <li>Резервное копирование данных.</li>
            </ul>
          </section>

          {/* 8. Права субъекта */}
          <section className="space-y-4 mb-8">
            <h2 className="text-xl font-semibold border-b border-border pb-2">
              8. Права субъекта персональных данных
            </h2>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Пользователь имеет право:
            </p>
            <ul className="text-sm text-foreground/90 space-y-2 list-disc list-inside">
              <li>Получить информацию об обработке своих персональных данных;</li>
              <li>Требовать уточнения, блокирования или уничтожения персональных данных;</li>
              <li>Отозвать согласие на обработку персональных данных;</li>
              <li>Обратиться в Роскомнадзор в случае нарушения своих прав.</li>
            </ul>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Отзыв согласия на обработку персональных данных влечёт прекращение
              предоставления услуг Сервиса и удаление учётной записи.
            </p>
          </section>

          {/* 9. Согласие при оформлении подписки */}
          <section className="space-y-4 mb-8">
            <h2 className="text-xl font-semibold border-b border-border pb-2">
              9. Согласие при оформлении подписки
            </h2>
            <p className="text-sm text-foreground/90 leading-relaxed">
              При оформлении платной подписки «Про» пользователь даёт отдельное
              согласие на обработку персональных данных, необходимых для:
            </p>
            <ul className="text-sm text-foreground/90 space-y-2 list-disc list-inside">
              <li>Проведения платежей через платёжную систему Т-Банк;</li>
              <li>Направления уведомлений об оплате, изменении условий и статусе подписки;</li>
              <li>Обработки обращений, связанных с возвратами.</li>
            </ul>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Данное согласие является обязательным условием для оформления подписки
              и подтверждается установкой отметки в соответствующем чек-боксе на
              странице оформления подписки.
            </p>
          </section>

          {/* 10. Контакты */}
          <section className="space-y-4 mb-8">
            <h2 className="text-xl font-semibold border-b border-border pb-2">
              10. Контактная информация
            </h2>
            <p className="text-sm text-foreground/90 leading-relaxed">
              По вопросам, связанным с обработкой персональных данных:
            </p>
            <ul className="text-sm text-foreground/90 space-y-2 list-disc list-inside">
              <li>Электронная почта: <strong>Epifanofe@mail.ru</strong></li>
              <li>Через раздел «Поддержка» в сервисе YaDela</li>
            </ul>
            <p className="text-sm text-foreground/90 leading-relaxed">
              Время обработки обращений: в рабочие дни с 10:00 до 19:00 (МСК).
              Срок ответа — не более 10 (десяти) рабочих дней.
            </p>
          </section>

          <div className="mt-10 p-4 rounded-lg bg-muted/50 border border-border">
            <p className="text-xs text-muted-foreground text-center">
              Устанавливая отметку в чек-боксе согласия при оформлении подписки,
              вы подтверждаете, что ознакомились с настоящей Политикой обработки
              персональных данных и даёте своё согласие на обработку данных
              в соответствии с указанными в ней условиями.
            </p>
          </div>
        </article>
      </div>
    </div>
  );
}
